import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  Home, Heart, MessageCircle, User, Search, Bell,
  Settings, HelpCircle, Gift, Sparkles
} from 'lucide-react';

// Import all Phase 2 components
import { 
  MobileBottomNav, 
  SwipeableCard, 
  VoiceInput, 
  FloatingActionButton,
  useTouchGestures
} from './MobileFirstComponents';

import { 
  NotificationProvider,
  useNotifications,
  GlobalToastContainer,
  GlobalCelebrationContainer,
  NotificationCenter,
  ContextualHelp
} from './SmartNotificationComponents';

import {
  ProgressiveProfileSetup,
  InteractiveTutorial,
  EnhancedEmailVerification
} from './EnhancedOnboardingComponents';

// Phase 2 Enhanced App Wrapper
export const Phase2AppWrapper = ({ children }) => {
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [contextualHelp, setContextualHelp] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Update active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') setActiveTab('home');
    else if (path === '/discover') setActiveTab('search');
    else if (path === '/speed-dating') setActiveTab('dating');
    else if (path === '/messages') setActiveTab('messages');
    else if (path === '/profile') setActiveTab('profile');
  }, [location.pathname]);

  // Handle tab navigation
  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.route);
  };

  // Handle first-time user tutorial
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    if (!hasSeenTutorial) {
      setTimeout(() => setShowTutorial(true), 2000);
    }
  }, []);

  const completeTutorial = () => {
    setShowTutorial(false);
    localStorage.setItem('hasSeenTutorial', 'true');
  };

  // Floating Action Button actions
  const fabActions = [
    {
      icon: <Heart className="w-4 h-4 text-white" />,
      label: 'Start Dating',
      color: 'bg-rose-500',
      onClick: () => navigate('/speed-dating')
    },
    {
      icon: <Bell className="w-4 h-4 text-white" />,
      label: 'Notifications',
      color: 'bg-blue-500',
      onClick: () => setShowNotificationCenter(true)
    },
    {
      icon: <HelpCircle className="w-4 h-4 text-white" />,
      label: 'Help',
      color: 'bg-green-500',
      onClick: () => setContextualHelp({
        steps: [
          {
            title: 'Need help?',
            description: 'We\'re here to help you make the most of Shot Clock Dating!',
            icon: <HelpCircle className="w-8 h-8 text-green-500" />
          },
          {
            title: 'Speed Dating Tips',
            description: 'Be yourself, ask engaging questions, and have fun!',
            tip: 'The 3-5 minute format helps you focus on real connections.'
          }
        ]
      })
    }
  ];

  return (
    <NotificationProvider>
      <div className="relative min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50">
        {/* Main Content */}
        <div className="pb-20 md:pb-0">
          {children}
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="md:hidden">
          <MobileBottomNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>

        {/* Floating Action Button */}
        <FloatingActionButton
          actions={fabActions}
          className="md:hidden"
        />

        {/* Global Notification Components */}
        <GlobalToastContainer />
        <GlobalCelebrationContainer />

        {/* Notification Center */}
        <NotificationCenter
          isOpen={showNotificationCenter}
          onClose={() => setShowNotificationCenter(false)}
        />

        {/* Contextual Help */}
        <AnimatePresence>
          {contextualHelp && (
            <ContextualHelp
              helpData={contextualHelp}
              onClose={() => setContextualHelp(null)}
            />
          )}
        </AnimatePresence>

        {/* Interactive Tutorial */}
        <AnimatePresence>
          {showTutorial && (
            <InteractiveTutorial
              onComplete={completeTutorial}
              tutorialSteps={[
                {
                  target: '[data-tutorial="home"]',
                  title: 'Welcome to Shot Clock Dating! 👋',
                  content: 'Let\'s take a quick tour to help you get started.',
                  placement: 'top'
                },
                {
                  target: '[data-tutorial="dating"]',
                  title: 'Speed Dating',
                  content: 'Start your 3-5 minute speed dating sessions here!',
                  placement: 'top'
                },
                {
                  target: '[data-tutorial="messages"]',
                  title: 'Messages',
                  content: 'Chat with your matches and build connections.',
                  placement: 'top'
                },
                {
                  target: '[data-tutorial="profile"]',
                  title: 'Your Profile',
                  content: 'Tap here to view and edit your profile anytime.',
                  placement: 'top'
                }
              ]}
            />
          )}
        </AnimatePresence>
      </div>
    </NotificationProvider>
  );
};

// Enhanced Homepage with Phase 2 Features
export const Phase2EnhancedHomepage = () => {
  const { addNotification, triggerCelebration } = useNotifications();
  const navigate = useNavigate();

  // Demo notification on page load
  useEffect(() => {
    const timer = setTimeout(() => {
      addNotification({
        type: 'match',
        title: 'New match! 💕',
        message: 'Sarah liked your profile! Start a conversation?',
        action: {
          label: 'Say Hi',
          handler: () => navigate('/messages')
        },
        isToast: true,
        duration: 5000
      });
    }, 3000);

    return () => clearTimeout(timer);
  }, [addNotification, navigate]);

  const handleStartDating = () => {
    triggerCelebration({
      type: 'achievement',
      title: 'Ready to Date! 🎉',
      message: 'Your journey to find love begins now!',
      duration: 2500
    });
    
    setTimeout(() => {
      navigate('/speed-dating');
    }, 2500);
  };

  return (
    <div className="space-y-8 p-4">
      {/* Hero Section with Tutorial Targets */}
      <div className="text-center py-12" data-tutorial="home">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-bold text-gray-800 mb-4"
        >
          Find Your Perfect Match
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-xl text-gray-600 mb-8"
        >
          AI-powered speed dating in 3-5 minutes
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            size="lg"
            onClick={handleStartDating}
            className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-lg px-8 py-4"
            data-tutorial="dating"
          >
            <Heart className="w-5 h-5 mr-2" />
            Start Speed Dating Now
          </Button>
        </motion.div>
      </div>

      {/* Swipeable Match Preview Cards */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800 text-center">
          Potential Matches
        </h2>
        
        <div className="max-w-sm mx-auto">
          {[
            { name: 'Sarah', age: 28, interests: 'Photography, Travel' },
            { name: 'Emma', age: 25, interests: 'Music, Art' },
            { name: 'Olivia', age: 30, interests: 'Fitness, Cooking' }
          ].map((person, index) => (
            <SwipeableCard
              key={index}
              onSwipeRight={() => {
                addNotification({
                  type: 'success',
                  title: 'It\'s a match! 💕',
                  message: `You and ${person.name} liked each other!`,
                  isToast: true,
                  duration: 3000
                });
              }}
              onSwipeLeft={() => {
                addNotification({
                  type: 'info',
                  title: 'Passed',
                  message: 'Looking for more matches...',
                  isToast: true,
                  duration: 2000
                });
              }}
              className="mb-4"
            >
              <Card className="bg-white shadow-lg">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="w-24 h-24 bg-gradient-to-r from-rose-200 to-pink-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-3xl">👤</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {person.name}, {person.age}
                    </h3>
                    <p className="text-gray-600 mt-2">{person.interests}</p>
                    <div className="flex justify-center space-x-4 mt-4">
                      <div className="flex items-center space-x-1 text-gray-500">
                        <Heart className="w-4 h-4" />
                        <span className="text-sm">Swipe right to like</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </SwipeableCard>
          ))}
        </div>
      </div>

      {/* Voice Input Demo */}
      <div className="max-w-md mx-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
          Voice Message
        </h3>
        <VoiceInput
          placeholder="Record a voice intro..."
          onTranscript={(text) => {
            addNotification({
              type: 'success',
              title: 'Voice message recorded! 🎤',
              message: text.substring(0, 50) + '...',
              isToast: true,
              duration: 3000
            });
          }}
        />
      </div>
    </div>
  );
};

// Tutorial targets for navigation
export const withTutorialTargets = (Component) => {
  return (props) => (
    <div>
      <Component {...props} />
      {/* Hidden tutorial target elements */}
      <div data-tutorial="messages" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
      <div data-tutorial="profile" style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
    </div>
  );
};

// Phase 2 Demo Page
export const Phase2Demo = () => {
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const navigate = useNavigate();

  const handleProfileSetupComplete = (profileData) => {
    console.log('Profile completed:', profileData);
    setShowProfileSetup(false);
    navigate('/');
  };

  const handleEmailVerified = () => {
    setShowEmailVerification(false);
    setShowProfileSetup(true);
  };

  if (showEmailVerification) {
    return (
      <EnhancedEmailVerification
        email="demo@example.com"
        onVerified={handleEmailVerified}
        onResend={() => Promise.resolve()}
      />
    );
  }

  if (showProfileSetup) {
    return (
      <ProgressiveProfileSetup
        onComplete={handleProfileSetupComplete}
      />
    );
  }

  return (
    <Phase2AppWrapper>
      <div className="min-h-screen">
        {/* Demo Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 text-center">
          <h1 className="text-2xl font-bold mb-2">🚀 Phase 2: Mobile & Interaction</h1>
          <p className="text-purple-100">
            Mobile-first enhancements, smart notifications, and enhanced onboarding
          </p>
        </div>

        {/* Demo Features */}
        <div className="p-6 space-y-6">
          <div className="grid gap-4">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">📱</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900">Mobile-First Enhancements</h3>
                    <p className="text-blue-700 text-sm">Touch gestures, bottom nav, voice input, haptic feedback</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <Bell className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-900">Smart Notifications</h3>
                    <p className="text-green-700 text-sm">Contextual help, celebrations, toast notifications</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-rose-500 rounded-full flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-rose-900">Enhanced Onboarding</h3>
                    <p className="text-rose-700 text-sm">Progressive setup, tutorials, email verification</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Demo Actions */}
          <div className="space-y-4">
            <Button
              onClick={() => setShowEmailVerification(true)}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
            >
              🎯 Demo Complete Onboarding Flow
            </Button>

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/speed-dating-demo')}
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                📱 Phase 1 Demo
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-green-500 text-green-500 hover:bg-green-50"
              >
                🏠 Return Home
              </Button>
            </div>
          </div>
        </div>

        <Phase2EnhancedHomepage />
      </div>
    </Phase2AppWrapper>
  );
};

export default {
  Phase2AppWrapper,
  Phase2EnhancedHomepage,
  withTutorialTargets,
  Phase2Demo
};